import { queryOptions } from "@tanstack/react-query";
import { api, unwrap } from "@/api/client";
import { qk } from "@/api/keys";
import { ApiError } from "@/api/problem";
import type { CurrentUser } from "@/api/types";

/**
 * `/v1/me` — the one query both the router and the React tree depend on.
 *
 * It lives on its own so that a route's `beforeLoad` (which runs OUTSIDE React and cannot
 * touch a context) and `useSession()` (which is inside it) read the SAME cache entry. If
 * they each had their own, a guard could admit you to a route that the component below it
 * then decides you cannot see — the classic flash-of-forbidden-content bug.
 */
export const meQueryOptions = queryOptions({
  queryKey: qk.me(),
  queryFn: async (): Promise<CurrentUser | null> => {
    try {
      return await unwrap(api.GET("/v1/me", {}));
    } catch (error) {
      // "Not signed in" is an answer, not a failure. Anything else is a real error and
      // must propagate — swallowing a 500 here would render the app as though it were
      // logged out, and send the user round a sign-in loop that cannot succeed.
      if (error instanceof ApiError && error.status === 401) return null;
      throw error;
    }
  },
  retry: false,
  staleTime: 30_000,
});
